import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import { messages, conversations } from '../mock';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { ChevronLeft, Send, MoreVertical, Image as ImageIcon } from 'lucide-react';
import { ScrollArea } from '../components/ui/scroll-area';
import { Separator } from '../components/ui/separator';

const MessagesPage = () => {
  const navigate = useNavigate();
  const [selectedChat, setSelectedChat] = useState(messages[0]?.id || null);
  const [newMessage, setNewMessage] = useState('');
  const [chatMessages, setChatMessages] = useState(conversations);

  const selectedConversation = messages.find((m) => m.id === selectedChat);
  const conversationMessages = chatMessages[selectedChat] || [];

  const handleSendMessage = (e) => {
    e.preventDefault();
    if (!newMessage.trim()) return;

    const newMsg = {
      id: `msg${Date.now()}`,
      senderId: 'currentUser',
      text: newMessage,
      timestamp: new Date().toISOString(),
    };

    setChatMessages({
      ...chatMessages,
      [selectedChat]: [...(chatMessages[selectedChat] || []), newMsg],
    });
    setNewMessage('');
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white rounded-xl shadow-sm overflow-hidden" style={{ height: 'calc(100vh - 200px)' }}>
          <div className="grid grid-cols-1 md:grid-cols-3 h-full">
            {/* Chat List - Left Sidebar */}
            <div className="border-r">
              <div className="p-4 border-b">
                <h2 className="text-xl font-bold text-gray-900">Messages</h2>
              </div>

              <ScrollArea className="h-full">
                {messages.map((message) => (
                  <div
                    key={message.id}
                    onClick={() => setSelectedChat(message.id)}
                    className={`p-4 border-b cursor-pointer hover:bg-gray-50 transition-colors ${
                      selectedChat === message.id ? 'bg-red-50' : ''
                    }`}
                  >
                    <div className="flex items-start space-x-3">
                      <Avatar className="w-12 h-12">
                        <AvatarImage src={message.senderAvatar} />
                        <AvatarFallback>{message.senderName[0]}</AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between mb-1">
                          <p className="font-semibold text-gray-900 truncate">
                            {message.senderName}
                          </p>
                          {message.unread && (
                            <span className="w-2 h-2 bg-red-500 rounded-full"></span>
                          )}
                        </div>
                        <p className="text-sm text-gray-600 truncate mb-1">
                          {message.listingTitle}
                        </p>
                        <p className="text-sm text-gray-500 truncate">{message.lastMessage}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </ScrollArea>
            </div>

            {/* Chat Window - Right Side */}
            <div className="col-span-2 flex flex-col">
              {selectedConversation ? (
                <>
                  {/* Chat Header */}
                  <div className="p-4 border-b flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <Avatar className="w-10 h-10">
                        <AvatarImage src={selectedConversation.senderAvatar} />
                        <AvatarFallback>{selectedConversation.senderName[0]}</AvatarFallback>
                      </Avatar>
                      <div>
                        <p className="font-semibold text-gray-900">
                          {selectedConversation.senderName}
                        </p>
                        <p className="text-sm text-gray-500">
                          {selectedConversation.listingTitle}
                        </p>
                      </div>
                    </div>
                    <Button variant="ghost" size="icon">
                      <MoreVertical className="w-5 h-5" />
                    </Button>
                  </div>

                  {/* Listing Card */}
                  <div className="p-4 bg-gray-50 border-b">
                    <div
                      onClick={() => navigate(`/listing/${selectedConversation.listingId}`)}
                      className="flex items-center space-x-3 bg-white rounded-lg p-3 cursor-pointer hover:shadow-md transition-shadow"
                    >
                      <div className="w-16 h-16 bg-gray-200 rounded-lg flex-shrink-0"></div>
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-gray-900 truncate">
                          {selectedConversation.listingTitle}
                        </p>
                        <p className="text-red-500 font-bold">$1,299</p>
                      </div>
                    </div>
                  </div>

                  {/* Messages */}
                  <ScrollArea className="flex-1 p-4">
                    <div className="space-y-4">
                      {conversationMessages.map((msg) => (
                        <div
                          key={msg.id}
                          className={`flex ${
                            msg.senderId === 'currentUser' ? 'justify-end' : 'justify-start'
                          }`}
                        >
                          <div
                            className={`max-w-xs md:max-w-md px-4 py-2 rounded-2xl ${
                              msg.senderId === 'currentUser'
                                ? 'bg-red-500 text-white'
                                : 'bg-gray-100 text-gray-900'
                            }`}
                          >
                            <p>{msg.text}</p>
                            <p
                              className={`text-xs mt-1 ${
                                msg.senderId === 'currentUser'
                                  ? 'text-red-100'
                                  : 'text-gray-500'
                              }`}
                            >
                              {new Date(msg.timestamp).toLocaleTimeString([], {
                                hour: '2-digit',
                                minute: '2-digit',
                              })}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </ScrollArea>

                  {/* Message Input */}
                  <div className="p-4 border-t">
                    <form onSubmit={handleSendMessage} className="flex items-center space-x-2">
                      <Button type="button" variant="ghost" size="icon">
                        <ImageIcon className="w-5 h-5 text-gray-500" />
                      </Button>
                      <Input
                        value={newMessage}
                        onChange={(e) => setNewMessage(e.target.value)}
                        placeholder="Type a message..."
                        className="flex-1"
                      />
                      <Button
                        type="submit"
                        className="bg-red-500 hover:bg-red-600 text-white"
                        disabled={!newMessage.trim()}
                      >
                        <Send className="w-5 h-5" />
                      </Button>
                    </form>
                  </div>
                </>
              ) : (
                <div className="flex-1 flex items-center justify-center text-gray-500">
                  <p>Select a conversation to start messaging</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MessagesPage;
